import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BookingPurchaseDialogData } from './BookingPurchaseDialog.types';
import { isEmptyValue } from './bookingGate';
import type {
  BookEventHostData,
  BookEventUser,
  EagreementsSnapshot,
  PaymentMethodOption,
} from './bookEvent.types';
import { canBookGuest, studioHasPenalties } from './bookEventRules';
import {
  buildBookFooterButton,
  buildDocumentLinks,
  buildPriceLabel,
  buildPriceLabelId,
  buildResponseErrors,
  buildResponseTitle,
  buildWarnings,
  resolveDialogView,
  type DialogProjectionState,
} from './dialogProjection';
import {
  buildDialogEventSummary,
  buildSpotAvailabilityProps,
  getCourseTime,
  getTrainersNames,
} from './eventSummary';
import {
  bookingAnalytics,
  isBookAppointment,
  type BookingOrchestrationVariant,
} from './bookingDialogVariant';
import { buildShareUrl, CLASSES_VIEW } from './shareBookingUrl';
import type { PriceBreakdownData } from './price-breakdown/PriceBreakdown';
import type { CourseScheduleDay } from '../event-details-summary/EventDetailsSummary';
import { t } from '../../services/locale';

function missingTrainerIds(
  data: BookEventHostData,
  trainerNames: Record<string, string>
): string[] {
  const ids = (data.event.scheduleToBook?.days || []).flatMap(
    (day) => day.trainers || []
  );
  return [...new Set(ids.filter((trainerId) => trainerNames[trainerId] === undefined))];
}

function useTrainerNames(data: BookEventHostData): Record<string, string> {
  const [trainerNames, setTrainerNames] = useState<Record<string, string>>({});
  const requestedTrainerIds = useRef(new Set<string>());
  const fetchTrainerName = useCallback(
    async (trainerId: string) => {
      try {
        const response = await data.getTrainerById(trainerId);
        setTrainerNames((current) => ({
          ...current,
          [trainerId]: response?.name || '',
        }));
      } catch (trainerError) {
        console.log('Error getting trainer => ', trainerError);
        setTrainerNames((current) => ({
          ...current,
          [trainerId]: current[trainerId] ?? '',
        }));
      }
    },
    [data]
  );

  useEffect(() => {
    missingTrainerIds(data, trainerNames).forEach((trainerId) => {
      if (requestedTrainerIds.current.has(trainerId)) {
        return;
      }
      requestedTrainerIds.current.add(trainerId);
      void fetchTrainerName(trainerId);
    });
  }, [data, fetchTrainerName, trainerNames]);

  return trainerNames;
}

function courseDayTrainers(
  day: NonNullable<NonNullable<BookEventHostData['event']['scheduleToBook']>['days']>[number],
  data: BookEventHostData,
  trainerNames: Record<string, string>
): string {
  if (day.trainers?.length) {
    return day.trainers.map((id) => trainerNames[id] || '').join(', ');
  }
  return getTrainersNames(
    data.event,
    data.portalConfig.isShortTrainerNameEnabled,
    data.shortenTrainerName
  );
}

function buildCourseScheduleDays(
  data: BookEventHostData,
  trainerNames: Record<string, string>
): CourseScheduleDay[] {
  if (data.event.type !== 'course' || !data.event.scheduleToBook?.days) {
    return [];
  }
  return data.event.scheduleToBook.days.map((day) => ({
      dayLabel: data.weekDays[day.days || ''] || '',
      timeRange: getCourseTime(day, data.branch, data.formatUnix),
      trainers: courseDayTrainers(day, data, trainerNames),
  }));
}

function useCourseScheduleDays(data: BookEventHostData): CourseScheduleDay[] {
  const trainerNames = useTrainerNames(data);
  return useMemo(
    () => buildCourseScheduleDays(data, trainerNames),
    [data, data.event, trainerNames]
  );
}

function useShareBookingEvent(data: BookEventHostData) {
  const [shared, setShared] = useState(false);
  const shareBookingEvent = useCallback(() => {
    if (isEmptyValue(data.branch)) {
      return;
    }
    const url = buildShareUrl({
      absUrl: data.getAbsUrl(),
      branchId: data.branch?._id || '',
      event: data.event,
      classesView: data.branch?.configuration?.webportal?.classes_view,
      innerWidth: data.getWindowWidth(),
      startOfDayUnix: data.startOfDayUnix,
      views: CLASSES_VIEW,
    });
    data.copyToClipboard(url);
    data.trackEvent({ category: 'book-event', name: 'LINK_COPIED_TO_CLIPBOARD' });
    setShared(true);
    window.setTimeout(() => setShared(false), 3000);
  }, [data]);
  return { shared, shareBookingEvent };
}

function projectFooterButtons(args: {
  view: BookingPurchaseDialogData['view'];
  data: BookEventHostData;
  variant: BookingOrchestrationVariant;
  loggedIn: boolean;
  allowToBook: boolean;
  priceToPay: number | null;
  userHasCredits: boolean;
  goingToWaitingList: boolean;
  transactionInProgress: boolean;
  isPriceLoading: boolean;
  isPriceBreakdownLoading: boolean;
  eagreements: EagreementsSnapshot;
  onBook: () => void;
  onTryAgain: () => void;
}) {
  if (args.view === 'success') {
    return [
      buildBookFooterButton({
        button: 'done',
        onClick: args.data.onClose,
      }),
    ];
  }
  if (args.view === 'error') {
    return [
      buildBookFooterButton({
        button: 'tryAgain',
        onClick: args.onTryAgain,
      }),
    ];
  }
  if (!args.loggedIn) {
    return [
      buildBookFooterButton({
        button: 'signUp',
        onClick: args.data.showLoginRegister,
      }),
    ];
  }
  if (!args.allowToBook) {
    return [];
  }
  return [
    buildBookFooterButton({
      button: 'primary',
      priceToPay: args.priceToPay,
      userHasCredits: args.userHasCredits,
      goingToWaitingList: args.goingToWaitingList,
      transactionInProgress: args.transactionInProgress,
      isPriceLoading: isBookAppointment(args.variant) ? undefined : args.isPriceLoading,
      isPriceBreakdownLoading: args.isPriceBreakdownLoading,
      isMissingProfileDetailsBlockingBooking: args.eagreements.isMissingProfileDetails,
      loadingOutstandingDocuments: args.eagreements.loadingOutstandingDocuments,
      isEagreementsFlagResolved: args.eagreements.flagResolved,
      areDocumentsSigned: args.eagreements.documentsSigned,
      onClick: args.onBook,
    }),
  ];
}

function buildGuestCounter(args: BookEventProjectionArgs) {
  if (isBookAppointment(args.variant)) {
    return undefined;
  }
  const showGuestCounter = [
    args.loggedIn,
    args.allowToBook,
    canBookGuest(args.data.event, args.data.branch),
  ].every(Boolean);
  if (!showGuestCounter) {
    return undefined;
  }
  return {
    guests: args.guests,
    label: t('WEB_NUMBER_OF_ATTENDANTS'),
    decreaseDisabled: args.guests <= 0,
    countId: 'class-booking-guest-count',
    increaseId: 'class-booking-add-guest',
    onIncrease: args.increaseGuests,
    onDecrease: args.decreaseGuests,
  };
}

function buildTerms(args: BookEventProjectionArgs) {
  const showTerms = [args.loggedIn, args.allowToBook, args.firstBook].every(Boolean);
  if (!showTerms) {
    return undefined;
  }
  return {
    mode: 'payg' as const,
    items: [
      {
        key: 'payg',
        accepted: args.termsAccepted,
        agreeLabelKey: 'WEB_I_AGREE_TO',
        linkLabelKey: 'WEB_PAYG_PAYMENT_TERMS',
        checkboxId: 'class-booking-accept-terms',
        onChange: args.setTermsAccepted,
        onClickLink: args.data.showPaygTerms,
      },
    ],
  };
}

function buildPayment(args: BookEventProjectionArgs, showPayment: boolean) {
  if (!showPayment) {
    return null;
  }
  return {
    showPayment: true,
    selectedPaymentLabel: args.selectedPayment?.value || '',
    paymentOptions: args.paymentOptions,
    paymentDropdownOpen: args.paymentDropdownOpen,
    onTogglePaymentDropdown: () => {
      if (args.paymentOptions.length <= 1) {
        return;
      }
      if (!args.paymentDropdownOpen) {
        const analytics = bookingAnalytics(args.variant);
        args.data.trackEvent({
          category: analytics.category,
          name: analytics.paymentDropdown,
        });
      }
      args.setPaymentDropdownOpen(!args.paymentDropdownOpen);
    },
    onSelectPayment: (option: { id?: string; value: string }) => {
      const matched = args.paymentOptions.find(
        (item) => item.id === option.id || item.value === option.value
      );
      if (matched) {
        args.setPaymentMethod(matched);
      }
    },
  };
}

function projectMainSummary(args: {
  projection: BookEventProjectionArgs;
  state: DialogProjectionState;
  view: BookingPurchaseDialogData['view'];
  scheduleDays: CourseScheduleDay[];
  showAttendancePolicy: boolean;
}) {
  const { projection, state, view } = args;
  if (view !== 'main') {
    return {
      warnings: [],
      eventDetailsSummary: null,
      priceBreakdown: null,
    };
  }
  return {
    warnings: buildWarnings(state, {
      onOpenProfile: projection.data.openProfile,
    }),
    eventDetailsSummary: buildDialogEventSummary({
      data: projection.data,
      variant: projection.variant,
      scheduleDays: args.scheduleDays,
      goingToWaitingList: projection.goingToWaitingList,
      showAttendancePolicy: args.showAttendancePolicy,
    }),
    priceBreakdown: buildVisiblePriceBreakdown(projection),
  };
}

function buildVisiblePriceBreakdown(args: BookEventProjectionArgs) {
  const isVisible = [args.loggedIn, (args.priceToPay ?? 0) > 0].every(Boolean);
  if (!isVisible) {
    return null;
  }
  return {
    ...args.priceBreakdown,
    isLoading: args.isPriceBreakdownLoading,
  };
}

function projectMainActions(args: {
  projection: BookEventProjectionArgs;
  state: DialogProjectionState;
  view: BookingPurchaseDialogData['view'];
}) {
  const { projection, state, view } = args;
  if (view !== 'main') {
    return {
      guestCounter: undefined,
      payment: null,
      terms: undefined,
      spotAvailability: null,
      documentLinks: [],
    };
  }
  return {
    guestCounter: buildGuestCounter(projection),
    payment: buildPayment(projection, state.showPayment),
    terms: buildTerms(projection),
    spotAvailability: isBookAppointment(projection.variant)
      ? null
      : buildSpotAvailabilityProps({
          event: projection.data.event,
          flags: projection.data.flags,
          userToken: projection.data.userToken,
          envUrl: projection.data.envUrl,
          branch: projection.data.branch,
          isSpotBookingEnabledForEvent: projection.data.isSpotBookingEnabledForEvent(
            projection.data.event
          ),
        }),
    documentLinks: buildDocumentLinks(state, (document) => {
      projection.openDocument(document.documentUrl || '', document.name);
    }),
  };
}

function projectResponse(
  args: BookEventProjectionArgs,
  state: DialogProjectionState,
  view: BookingPurchaseDialogData['view']
) {
  if (view === 'main') {
    return {
      responseTitle: '',
      responseName: '',
      responseErrors: [],
    };
  }
  return {
    responseTitle: buildResponseTitle(view, state),
    responseName: args.data.event.name || '',
    responseErrors: view === 'error' ? buildResponseErrors(state) : [],
  };
}

type BookEventProjectionArgs = {
  data: BookEventHostData;
  variant: BookingOrchestrationVariant;
  user: BookEventUser | null;
  loggedIn: boolean;
  allowToBook: boolean;
  userHasCredits: boolean;
  goingToWaitingList: boolean;
  firstBook: boolean;
  guests: number;
  increaseGuests: () => void;
  decreaseGuests: () => void;
  priceToPay: number | null;
  creditsToPay: number;
  paymentOptions: PaymentMethodOption[];
  selectedPayment: PaymentMethodOption | undefined;
  paymentDropdownOpen: boolean;
  setPaymentDropdownOpen: (open: boolean) => void;
  setPaymentMethod: (option: PaymentMethodOption) => void;
  isPriceLoading: boolean;
  isPriceBreakdownLoading: boolean;
  priceBreakdown: PriceBreakdownData | null;
  eagreements: EagreementsSnapshot;
  openDocument: (documentUrl: string, name?: string) => void;
  termsAccepted: boolean;
  setTermsAccepted: (accepted: boolean) => void;
  success: boolean;
  error: boolean;
  errors: string[];
  messagesData: Record<string, unknown>;
  transactionInProgress: boolean;
  bookEvent: () => Promise<void>;
  tryAgain: () => void;
};

function buildProjectionState(args: BookEventProjectionArgs): DialogProjectionState {
  return {
    success: args.success,
    error: args.error,
    title: args.data.event.name || '',
    isUserLogIn: args.loggedIn,
    isAllowToBook: args.allowToBook,
    showPrice: args.data.portalConfig.isPriceEnabled,
    userHasCredits: args.userHasCredits,
    isGoingToWaitingList: args.goingToWaitingList,
    priceToPay: args.priceToPay,
    creditsToPay: args.creditsToPay,
    outstandingDocuments: args.eagreements.outstandingDocuments,
    errors: args.errors,
    messagesData: args.messagesData,
    isMissingProfileDetails: args.eagreements.isMissingProfileDetails,
    showPayment: [args.loggedIn, args.allowToBook, args.paymentOptions.length > 0].every(Boolean),
  };
}

function shouldShowAttendancePolicy(args: BookEventProjectionArgs): boolean {
  if (isBookAppointment(args.variant)) {
    return false;
  }
  const freeWithoutCredits = !args.userHasCredits && args.priceToPay === 0;
  return [args.loggedIn, !freeWithoutCredits, studioHasPenalties(args.data.penalties)].every(Boolean);
}

function buildHeader(
  args: BookEventProjectionArgs,
  shared: boolean,
  shareBookingEvent: () => void
) {
  const { data } = args;
  const appointment = isBookAppointment(args.variant);
  return {
    showShare: appointment ? false : [!args.success, !args.error].every(Boolean),
    showClose: true,
    shared,
    sharedTooltipText: t('WEB_LINK_COPIED_TO_CLIPBOARD'),
    onShare: appointment ? undefined : shareBookingEvent,
    onClose: data.onClose,
    closeButtonId: appointment ? 'close-book-appointment-btn' : 'close-book-event-btn',
    closeAriaLabel: t('WEB_CLOSE') || 'Close',
    shareAriaLabel: t('WEB_SHARE') || 'Share',
    imageUrl: data.event.image_url || '',
  };
}

export function useBookEventProjection(
  args: BookEventProjectionArgs
): BookingPurchaseDialogData {
  const { data } = args;
  const event = data.event;
  const scheduleDays = useCourseScheduleDays(data);
  const { shared, shareBookingEvent } = useShareBookingEvent(data);
  const projectionState = buildProjectionState(args);
  const view = resolveDialogView(projectionState);
  const showAttendancePolicy = shouldShowAttendancePolicy(args);

  const footerButtons = projectFooterButtons({
    ...args,
    view,
    onBook: () => void args.bookEvent(),
    onTryAgain: args.tryAgain,
  });
  const mainSummary = projectMainSummary({
    projection: args,
    state: projectionState,
    view,
    scheduleDays,
    showAttendancePolicy,
  });
  const mainActions = projectMainActions({
    projection: args,
    state: projectionState,
    view,
  });
  const response = projectResponse(args, projectionState, view);

  return {
    variant: args.variant,
    unifiedDialogEnabled: true,
    view,
    header: buildHeader(args, shared, shareBookingEvent),
    title: projectionState.title,
    priceLabel: buildPriceLabel(projectionState),
    priceLabelId: buildPriceLabelId(projectionState),
    warnings: mainSummary.warnings,
    description: { description: event.description || '', previewLength: 140 },
    eventDetailsSummary: mainSummary.eventDetailsSummary,
    priceBreakdown: mainSummary.priceBreakdown,
    ...mainActions,
    footerButtons,
    ...response,
  };
}
